import { useState, useEffect, useCallback } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Info,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  TrendingDown,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Scraper Health Cards — Part 2 of the Admin Observability spec (Calvin,
 * 2026-02-28). One card per active scraper (news, cruise, fuel, lottery,
 * weather). Each card shows: last successful run, last-run status,
 * row-count delta vs previous run (flagged >20% drop — the threshold
 * that would have caught the cruise regex regression earlier), next
 * scheduled fire time. Manual "Run now" button per card. Click to
 * expand the last 5 run-history entries.
 *
 * Out of scope: no automated alerting on failure. Visibility only.
 */

const LEVEL_STYLES = {
  critical: {
    border: "border-l-[#E63946]",
    bg: "bg-[#FEE2E2]/40",
    text: "text-[#7F1D1D]",
    icon: AlertCircle,
    dot: "#E63946",
    label: "CRITICAL",
  },
  warn: {
    border: "border-l-[#F59E0B]",
    bg: "bg-[#FEF3C7]/60",
    text: "text-[#92400E]",
    icon: AlertTriangle,
    dot: "#F59E0B",
    label: "DEGRADED",
  },
  ok: {
    border: "border-l-[#3BB273]",
    bg: "bg-[#DCFCE7]/40",
    text: "text-[#166534]",
    icon: CheckCircle2,
    dot: "#3BB273",
    label: "HEALTHY",
  },
  // Neutral state — source is up but has nothing to publish right now
  // (mid-cycle for rotating rosters like pharmacy on duty, or an
  // unchanged monthly fuel price with a fresh cache). Distinct from
  // both green (wrote rows) and yellow (partial failure).
  info: {
    border: "border-l-[#9CA3AF]",
    bg: "bg-[#F3F4F6]/60",
    text: "text-[#374151]",
    icon: Info,
    dot: "#9CA3AF",
    label: "NO NEW DATA",
  },
  // Intentionally paused — scraper is not participating in the urgency
  // signal (e.g. carnival_sources through Nov 2026 while manual entry
  // is the source of truth). Visually distinct from critical/warn so
  // admins don't chase a false alarm, but distinct from ok/info too so
  // it doesn't look "fine" — the amber left border + dashed accent
  // reads as "held, not passing." Underlying failure state stays
  // visible if you expand the history.
  paused: {
    border: "border-l-[#F59E0B] border-l-dashed",
    bg: "bg-[#F9FAFB]",
    text: "text-[#6B7280]",
    icon: PauseCircle,
    dot: "#F59E0B",
    label: "PAUSED",
  },
  // Known external outage — the source's own hosting is unreachable
  // (confirmed via TCP-handshake timeout from our pod). Retry logic is
  // running and functioning correctly, but no amount of retry can fix
  // a network-level block. Visually reads as muted amber (same family
  // as paused, but a solid border to distinguish "waiting for source"
  // from "we've stopped this on purpose"). Auto-clears the moment a
  // run succeeds — this is a display-only downgrade, not a permanent
  // override.
  known_outage: {
    border: "border-l-[#F59E0B]",
    bg: "bg-[#F9FAFB]",
    text: "text-[#92400E]",
    icon: AlertTriangle,
    dot: "#F59E0B",
    label: "SOURCE OFFLINE",
  },
};

const timeAgo = (iso) => {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const timeUntil = (iso) => {
  if (!iso) return "unscheduled";
  const t = new Date(iso).getTime();
  const s = Math.floor((t - Date.now()) / 1000);
  if (s < 0) return "due now";
  if (s < 60) return `in ${s}s`;
  if (s < 3600) return `in ${Math.floor(s / 60)}m`;
  if (s < 86400) return `in ${Math.floor(s / 3600)}h`;
  return `in ${Math.floor(s / 86400)}d`;
};

const Card = ({ scraper, onRunNow, busy }) => {
  const [expanded, setExpanded] = useState(false);
  const style = LEVEL_STYLES[scraper.level] || LEVEL_STYLES.ok;
  const Icon = style.icon;
  const delta = scraper.row_delta_pct;
  const bigDrop = delta !== null && delta !== undefined && delta <= -20;
  const paused = !!scraper.paused;
  const outageActive = !!scraper.outage_active;
  return (
    <div
      className={`rounded-lg border-l-4 ${style.border} ${style.bg} border border-[#EEEEEE] overflow-hidden`}
      data-testid={`scraper-card-${scraper.key}`}
      data-scraper-level={scraper.level}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Icon size={16} className={`${style.text} mt-0.5 shrink-0`} />
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-[#1A1A1A] truncate">
                {scraper.label}
              </div>
              <div className={`text-[10px] uppercase tracking-widest font-semibold mt-0.5 ${style.text}`}>
                {style.label}{!paused && scraper.stale ? " · STALE" : ""}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onRunNow(scraper.key)}
            disabled={busy}
            data-testid={`scraper-run-now-${scraper.key}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] bg-[#0AADA0]/10 text-[#0AADA0] hover:bg-[#0AADA0]/20 disabled:opacity-50 whitespace-nowrap"
          >
            {busy ? <RefreshCw size={11} className="animate-spin" /> : <PlayCircle size={11} />}
            {busy ? "Starting…" : "Run now"}
          </button>
        </div>
        <div className="mt-3 space-y-1 text-[12px] text-[#374151]">
          {paused && scraper.pause_reason && (
            <div
              className="text-[11px] text-[#92400E] bg-[#FEF3C7]/70 border border-[#FDE68A] rounded px-2 py-1.5 mb-1"
              data-testid={`scraper-pause-reason-${scraper.key}`}
            >
              <span className="font-semibold">Paused — see backlog:</span>{" "}
              {scraper.pause_reason}
            </div>
          )}
          {outageActive && scraper.outage_reason && (
            <div
              className="text-[11px] text-[#92400E] bg-[#FEF3C7]/70 border border-[#FDE68A] rounded px-2 py-1.5 mb-1"
              data-testid={`scraper-outage-reason-${scraper.key}`}
            >
              <span className="font-semibold">Source offline:</span>{" "}
              {scraper.outage_reason}
            </div>
          )}
          <div className="flex items-center gap-2">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: style.dot }}
            />
            <span>Last success: <strong>{timeAgo(scraper.last_success_at)}</strong></span>
          </div>
          {scraper.last_run_at && scraper.last_run_at !== scraper.last_success_at && (
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#9CA3AF]" />
              <span>
                Last attempt: {timeAgo(scraper.last_run_at)}
                {scraper.last_run_duration_s != null && (
                  <> · {Math.round(scraper.last_run_duration_s)}s</>
                )}
                {scraper.last_status && scraper.last_status !== "ok" && (
                  <> · <span className={style.text}>{scraper.last_status}</span></>
                )}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#9CA3AF]" />
            <span>
              Rows this run: <strong>{scraper.row_count}</strong>
              {delta !== null && delta !== undefined && (
                <span className={`ml-2 inline-flex items-center gap-0.5 ${bigDrop ? "text-[#E63946] font-semibold" : "text-[#6B7280]"}`}>
                  {bigDrop && <TrendingDown size={10} />}
                  {delta > 0 ? "+" : ""}{delta}% vs prev
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[#6B7280]">
            <Clock size={10} />
            <span>Next: {timeUntil(scraper.next_run_at)}</span>
          </div>
          {!paused && !outageActive && scraper.consecutive_failures > 0 && (
            <div className={`flex items-center gap-2 ${style.text}`}>
              <AlertTriangle size={10} />
              <span>{scraper.consecutive_failures} consecutive failure{scraper.consecutive_failures > 1 ? "s" : ""}</span>
            </div>
          )}
          {scraper.last_error && !paused && !outageActive && (
            <div
              className={`text-[11px] mt-1 truncate ${scraper.level === "info" ? "text-[#6B7280]" : "text-[#E63946]"}`}
              title={scraper.last_error}
            >
              {scraper.level === "info" ? "Reason: " : "Error: "}{scraper.last_error}
            </div>
          )}
        </div>
        {(scraper.history || []).length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`scraper-history-toggle-${scraper.key}`}
            className="mt-3 text-[11px] text-[#0AADA0] hover:underline flex items-center gap-1"
          >
            {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            {expanded ? "Hide" : "Show"} last {scraper.history.length} runs
          </button>
        )}
        {expanded && (
          <ul className="mt-2 space-y-1 text-[11px] font-mono text-[#4B5563]" data-testid={`scraper-history-${scraper.key}`}>
            {scraper.history.map((h, i) => (
              <li key={i} className="border-t border-[#E5E7EB] pt-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span>{timeAgo(h.ran_at)}</span>
                  <span className={
                    h.status === "ok" ? "text-[#166534]"
                    : h.status === "no_data" ? "text-[#6B7280]"
                    : h.status === "partial" ? "text-[#92400E]"
                    : "text-[#7F1D1D]"
                  }>{h.status}</span>
                  <span>· {h.rows_written} rows</span>
                  <span>· {Math.round(h.duration_s || 0)}s</span>
                </div>
                {h.error && (
                  <div className="text-[#E63946] truncate" title={h.error}>
                    {h.error}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export const ScraperHealthCards = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const [reloading, setReloading] = useState(false);

  const load = useCallback(async () => {
    setReloading(true);
    try {
      const r = await api.get("/admin/health/scrapers");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load scraper health");
    } finally {
      setReloading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async (key) => {
    setBusy((s) => ({ ...s, [key]: true }));
    try {
      await api.post(`/admin/health/scrapers/${key}/run-now`);
      toast.success(`${key}: started in background — refresh in ~30s to see updated stats`);
      // Poll once after a delay so admin sees the new state without a manual reload
      setTimeout(load, 15000);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Run now failed for ${key}`);
    } finally {
      setBusy((s) => ({ ...s, [key]: false }));
    }
  };

  if (!data) return null;
  return (
    <div className="card-soft p-5" data-testid="scraper-health-cards">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm uppercase tracking-wider text-[#1A1A1A]">
            Scraper Status
          </h3>
          <p className="text-[11px] text-[#6B7280] mt-1">
            One card per scraper. Red = failed or no successful run in 24h+.
            Yellow = partial or ≥20% drop from previous run. Green = healthy.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={reloading}
          data-testid="scraper-health-reload"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-[#F3F4F6] text-[#374151] hover:bg-[#E5E7EB] disabled:opacity-50"
        >
          <RefreshCw size={12} className={reloading ? "animate-spin" : ""} />
          {reloading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {(data.scrapers || []).map((s) => (
          <Card
            key={s.key}
            scraper={s}
            onRunNow={runNow}
            busy={!!busy[s.key]}
          />
        ))}
      </div>
    </div>
  );
};
